/**
 * Helpers for dev diagnostics (e.g. read session from storage without `getSession()`).
 */

/**
 * Read persisted GoTrue session access_token without calling `supabase.auth.getSession()`.
 * Matches default storage key: `sb-${supabaseHostnameFirstLabel}-auth-token` (see @supabase/supabase-js).
 * @param {string} supabaseUrl
 * @returns {string | null}
 */
export function readSupabaseAuthAccessTokenFromLocalStorage(supabaseUrl) {
  if (typeof window === 'undefined' || !supabaseUrl) return null;
  try {
    const hostname = new URL(supabaseUrl.trim()).hostname;
    const ref = hostname.split('.')[0];
    const key = `sb-${ref}-auth-token`;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.access_token === 'string' ? parsed.access_token : null;
  } catch {
    return null;
  }
}

/** @param {string} accessToken */
function readJwtExpSeconds(accessToken) {
  try {
    const part = accessToken.split('.')[1];
    if (!part) return 0;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (base64.length % 4)) % 4;
    const padded = base64 + '='.repeat(pad);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' ? payload.exp : 0;
  } catch {
    return 0;
  }
}

/**
 * Same as {@link readSupabaseAuthAccessTokenFromLocalStorage} but returns null if JWT `exp` is
 * missing or within `minTtlSeconds` of now (so callers can fall back to `getSession` / refresh).
 * @param {string} supabaseUrl
 * @param {number} [minTtlSeconds]
 * @returns {string | null}
 */
export function readValidSupabaseAccessTokenFromLocalStorage(supabaseUrl, minTtlSeconds = 90) {
  const token = readSupabaseAuthAccessTokenFromLocalStorage(supabaseUrl);
  if (!token) return null;
  const exp = readJwtExpSeconds(token);
  if (!exp) return token;
  const now = Math.floor(Date.now() / 1000);
  if (exp < now + minTtlSeconds) return null;
  return token;
}
